import React, { useEffect, useState } from 'react';
import { Source, Layer, Popup, useMap } from 'react-map-gl';
import styled from 'styled-components';

const rectangleData = [
  { id: '22', coordinates: [26.7159, 58.3784], name: 'Tallinn - EETT' },
  { id: '29', coordinates: [16.9333, 52.4000], name: 'Warsaw - EPWW' },
  { id: '11', coordinates: [-4.5000, 59.5000], name: 'Scottish - EGPX' },
  { id: '26', coordinates: [9.9500, 52.4000], name: 'Bremen - EDWW' },
  { id: '32', coordinates: [4.5000, 52.5000], name: 'Amsterdam - EHAA' },
  { id: '10', coordinates: [-3.5, 51], name: 'London - EGTT' }
];

const StyledFmpPopup = styled(Popup)`
  .mapboxgl-popup-content {
    background: #1e1e2f;
    color: #fff;
    font-family: 'Roboto', sans-serif;
    border-radius: 12px;
    padding: 12px;
    width: 250px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.3);
  }

  .popup-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    font-size: 1rem;
    font-weight: 700;
  }

  .popup-count {
    color: #00d1d1;
    font-size: 0.85rem;
  }

  .flow-measure {
    background: #2a2a40;
    border-radius: 8px;
    padding: 6px 8px;
    margin-bottom: 6px;
    font-size: 0.85rem;
    box-shadow: 0 2px 6px rgba(0,0,0,0.2);
  }

  .measure-status {
    font-size: 0.75rem;
  }

  .measure-active { color: #fff; }
  .measure-planned { color: #3b82f6; }
  .measure-ended { color: #ccc; }

  button.close-btn {
    background: #d63c3c;
    border: none;
    border-radius: 50%;
    width: 22px;
    height: 22px;
    color: #fff;
    font-weight: bold;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  }

  .mapboxgl-popup-tip {
    border-top-color: #1e1e2f !important;
  }
`;

const FmpComponent = () => {
  const [flowData, setFlowData] = useState([]);
  const [selectedRectangle, setSelectedRectangle] = useState(null);
  const { current: map } = useMap();

  useEffect(() => {
    const fetchFlowData = () => {
      fetch('https://ecfmp.vatsim.net/api/v1/flow-measure')
        .then(res => res.json())
        .then(data => setFlowData(data))
        .catch(err => console.error(err));
    };

    fetchFlowData();
    const interval = setInterval(fetchFlowData, 120000);
    return () => clearInterval(interval);
  }, []);

  const handleRectangleClick = (rectangle) => setSelectedRectangle(rectangle);
  const handleClosePopup = () => setSelectedRectangle(null);

  const getFlowDataForRectangle = (rectangleId) => {
    const data = flowData.filter(item => item.notified_flight_information_regions.includes(parseInt(rectangleId)));
    return data.length ? data : null;
  };

  useEffect(() => {
    if (!map) return;

    rectangleData.forEach(rect => {
      map.on('mouseenter', `rectangle-image-${rect.id}`, () => map.getCanvas().style.cursor = 'pointer');
      map.on('mouseleave', `rectangle-image-${rect.id}`, () => map.getCanvas().style.cursor = '');
      map.on('click', `rectangle-image-${rect.id}`, () => handleRectangleClick(rect));
    });

    return () => {
      rectangleData.forEach(rect => {
        map.off('mouseenter', `rectangle-image-${rect.id}`);
        map.off('mouseleave', `rectangle-image-${rect.id}`);
        map.off('click', `rectangle-image-${rect.id}`);
      });
    };
  }, [map, flowData]);

  const formatToDDHHMMZ = (date) => {
    const d = date.getUTCDate().toString().padStart(2, '0');
    const h = date.getUTCHours().toString().padStart(2, '0');
    const m = date.getUTCMinutes().toString().padStart(2, '0');
    return `${d}${h}${m}Z`;
  };

  const renderFlowData = (flowData) => flowData.map((flow, idx) => {
    const now = new Date();
    const start = new Date(flow.starttime);
    const end = new Date(flow.endtime);
    const withdrawn = flow.withdrawn_at ? new Date(flow.withdrawn_at) : null;

    let statusClass = 'measure-ended';
    let statusMessage = 'Ended';

    if (withdrawn && now > withdrawn) {
      statusClass = 'measure-ended';
      statusMessage = `Withdrawn at ${formatToDDHHMMZ(withdrawn)}`;
    } else if (now < start) {
      statusClass = 'measure-planned';
      statusMessage = `Planned from ${formatToDDHHMMZ(start)}`;
    } else if (now >= start && now <= end) {
      statusClass = 'measure-active';
      statusMessage = `Active until ${formatToDDHHMMZ(end)}`;
    }

    const formatMeasureType = (type) => {
      switch (type) {
        case 'minimum_departure_interval': return 'Tactical MDI';
        case 'miles_in_trail': return 'Miles in Trail';
        case 'mandatory_route': return 'Mandatory Route';
        default: return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      }
    };

    const formatMDIValue = (value) => value ? `${(value / 60).toFixed(1)} min` : 'N/A';

    return (
      <div key={idx} className="flow-measure">
        <div className="mb-1">
          <strong>{formatMeasureType(flow.measure?.type)}</strong>
          <p className={`measure-status ${statusClass}`}>{statusMessage}</p>
        </div>
        <div>{flow.measure?.type === 'minimum_departure_interval' ? formatMDIValue(flow.measure.value) : flow.measure?.value}</div>
        <div><strong>Reason:</strong> {flow.reason}</div>
        {flow.filters?.map((filter, i) => (
          <div key={i}><strong>{filter.type}:</strong> {filter.value.join(', ')}</div>
        ))}
      </div>
    );
  });

  const getMeasureCountForRectangle = (id) => {
    const data = getFlowDataForRectangle(id);
    return data ? data.length : 0;
  };

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">Map with Flow Data</h2>

      {rectangleData.map(rect => (
        <Source
          key={rect.id}
          id={`rectangle-source-${rect.id}`}
          type="geojson"
          data={{
            type: 'FeatureCollection',
            features: [{
              type: 'Feature',
              geometry: { type: 'Point', coordinates: rect.coordinates },
              properties: { id: rect.id, name: rect.name }
            }]
          }}
        >
          <Layer
            id={`rectangle-image-${rect.id}`}
            type="symbol"
            layout={{
              'icon-image': 'ecfmp-logo-3',
              'icon-size': 0.15,
              'icon-anchor': 'center',
              'icon-allow-overlap': true,
              'text-allow-overlap': true
            }}
            minzoom={3}
          />
        </Source>
      ))}

      {selectedRectangle && (
        <StyledFmpPopup
          longitude={selectedRectangle.coordinates[0]}
          latitude={selectedRectangle.coordinates[1]}
          closeOnClick={false}
          anchor="top"
          closeButton={false}
        >
          <div className="popup-header">
            <span>{selectedRectangle.name}</span>
            <span className="popup-count">({getMeasureCountForRectangle(selectedRectangle.id)})</span>
            <button className="close-btn" onClick={handleClosePopup}>×</button>
          </div>

          <div>
            {getFlowDataForRectangle(selectedRectangle.id) ? renderFlowData(getFlowDataForRectangle(selectedRectangle.id))
              : <p className="text-gray-400 text-sm">No Flow Measures for this region</p>}
          </div>
        </StyledFmpPopup>
      )}
    </div>
  );
};

export default FmpComponent;
